import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, Send, AlertTriangle, Search } from 'lucide-react';
import { api } from '../../api/client';

type Tenant = { id: string; name: string };

type LineItem = {
  name: string;
  code: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  discountPct: number;
  vatCategory: string;
  vatPercent: number;
};

const emptyLine = (): LineItem => ({
  name: '',
  code: '',
  quantity: 1,
  unit: 'H87',
  unitPrice: 0,
  discountPct: 0,
  vatCategory: 'S',
  vatPercent: 20,
});

const documentTypes = [
  { value: '380', label: 'Faktura' },
  { value: '381', label: 'Knjižno odobrenje' },
  { value: '383', label: 'Knjižno zaduženje' },
  { value: '386', label: 'Avansna' },
];

const paymentMeans = [
  { value: '30', label: 'Virman' },
  { value: '10', label: 'Gotovina' },
  { value: '48', label: 'Kartica' },
];

const units = [
  { value: 'H87', label: 'H87 (kom)' },
  { value: 'KGM', label: 'KGM (kg)' },
  { value: 'LTR', label: 'LTR (l)' },
  { value: 'MTR', label: 'MTR (m)' },
];

const vatCategories = [
  { value: 'S', label: 'S (standardna)' },
  { value: 'AE', label: 'AE (obrnuto)' },
  { value: 'E', label: 'E (oslobođeno)' },
  { value: 'O', label: 'O (van sistema)' },
  { value: 'Z', label: 'Z (nulta)' },
];

export function NewInvoicePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cloneId = searchParams.get('clone');
  const cloneType = searchParams.get('type') as '381' | '383' | null;
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [documentType, setDocumentType] = useState('380');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issues, setIssues] = useState<{ field: string; message: string }[] | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  // Buyer
  const [buyerTaxId, setBuyerTaxId] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [buyerRegNumber, setBuyerRegNumber] = useState('');
  const [buyerAddress, setBuyerAddress] = useState('');
  const [buyerCity, setBuyerCity] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [buyerJbkjs, setBuyerJbkjs] = useState('');
  const [isBudgetUser, setIsBudgetUser] = useState(false);
  const [hasJbkjs, setHasJbkjs] = useState(false);

  // Dates
  const today = new Date().toISOString().slice(0, 10);
  const [issueDate, setIssueDate] = useState(today);
  const [dueDate, setDueDate] = useState(today);
  const [deliveryDate, setDeliveryDate] = useState(today);

  // Payment
  const [paymentMeansCode, setPaymentMeansCode] = useState('30');
  const [bankAccount, setBankAccount] = useState('');

  // References
  const [orderReference, setOrderReference] = useState('');
  const [contractReference, setContractReference] = useState('');
  const [previousInvoice, setPreviousInvoice] = useState('');

  // Lines
  const [lines, setLines] = useState<LineItem[]>([emptyLine()]);

  useEffect(() => {
    api.get<Tenant[]>('/admin/tenants').then((rows) => {
      setTenants(rows);
      if (rows.length > 0) setSelectedTenantId(rows[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!cloneId || !cloneType) return;
    api.get<any>(`/admin/efaktura/invoices/${cloneId}`).then((src) => {
      if (!src) return;
      setSelectedTenantId(src.tenantId);
      setDocumentType(cloneType);
      // Leave invoice number empty - user must enter a new one
      setBuyerTaxId(src.buyerTaxId || '');
      setBuyerName(src.buyerName || '');
      setBuyerRegNumber(src.buyerRegNumber || '');
      setBuyerAddress(src.buyerAddress || '');
      setBuyerCity(src.buyerCity || '');
      setBuyerEmail(src.buyerEmail || '');
      setBuyerJbkjs(src.buyerJbkjs || '');
      setHasJbkjs(!!src.buyerJbkjs);
      setIsBudgetUser(!!src.isBudgetClient);
      setPaymentMeansCode(src.paymentMeansCode || '30');
      setBankAccount(src.bankAccount || '');
      setOrderReference(src.purchaseOrderRef || '');
      setContractReference(src.contractRef || '');
      setPreviousInvoice(src.invoiceNumber || '');
      if (Array.isArray(src.lines) && src.lines.length > 0) {
        setLines(src.lines.map((l: any) => ({
          name: l.productName || '',
          code: l.productCode || '',
          quantity: Number(l.quantity) || 0,
          unit: l.unitCode || 'H87',
          unitPrice: Number(l.unitPrice) || 0,
          discountPct: Number(l.discountPct) || 0,
          vatCategory: l.vatCategory || 'S',
          vatPercent: Number(l.vatPercent) || 20,
        })));
      }
    }).catch((e: any) => {
      setError(e?.data?.message || e?.message || 'Greška pri učitavanju originalne fakture');
    });
  }, [cloneId, cloneType]);

  const updateLine = (idx: number, key: keyof LineItem, value: any) => {
    setLines((prev) => prev.map((l, i) => i === idx ? { ...l, [key]: value } : l));
  };

  const removeLine = (idx: number) => {
    setLines((prev) => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
  };

  const lineTotal = (l: LineItem) => l.quantity * l.unitPrice * (1 - l.discountPct / 100);
  const lineVat = (l: LineItem) => lineTotal(l) * l.vatPercent / 100;

  const totalWithoutVat = lines.reduce((s, l) => s + lineTotal(l), 0);
  const totalVat = lines.reduce((s, l) => s + lineVat(l), 0);
  const grandTotal = totalWithoutVat + totalVat;

  const formatAmount = (n: number) =>
    new Intl.NumberFormat('sr-Latn-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

  const handleLookup = async () => {
    if (!buyerTaxId.trim()) return;
    setLookupLoading(true);
    setError(null);
    try {
      // Ruta prima `vatId` + `tenantId` i vraca `{ found, company }`. Ranije je
      // ovde islo `{ taxId }` i citalo se `data.name`, pa pretraga nikad nije
      // nista popunila (uvek „PIB mora imati tacno 9 cifara").
      const data = await api.post<{
        found: boolean;
        company: { name?: string; registrationNumber?: string | null; deleted?: boolean } | null;
        registeredOnSef: boolean | null;
      }>('/admin/efaktura/company-lookup', { tenantId: selectedTenantId, vatId: buyerTaxId.trim() });
      if (!data.found || !data.company) {
        setError('Firma sa ovim PIB-om nije nađena u registru SEF-a.');
        return;
      }
      if (data.company.name) setBuyerName(data.company.name);
      if (data.company.registrationNumber) setBuyerRegNumber(data.company.registrationNumber);
      if (data.company.deleted) setError('Pažnja: firma je obrisana iz registra SEF-a.');
      else if (data.registeredOnSef === false) setError('Pažnja: firma nije registrovana na SEF-u - e-faktura joj se ne može poslati.');
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri pretraži firme');
    } finally {
      setLookupLoading(false);
    }
  };

  const buildPayload = () => ({
    tenantId: selectedTenantId,
    invoiceNumber: invoiceNumber.trim(),
    invoiceTypeCode: documentType,
    issueDate,
    dueDate: dueDate || undefined,
    deliveryDate: deliveryDate || undefined,
    currency: 'RSD',
    buyerName,
    buyerTaxId,
    buyerRegNumber: buyerRegNumber || undefined,
    buyerJbkjs: hasJbkjs ? buyerJbkjs : undefined,
    buyerAddress,
    buyerCity,
    buyerEmail: buyerEmail || undefined,
    isBudgetClient: isBudgetUser,
    paymentMeansCode,
    bankAccount: bankAccount || undefined,
    purchaseOrderRef: orderReference || undefined,
    contractRef: contractReference || undefined,
    precedingInvoice: previousInvoice || undefined,
    lines: lines.map((l) => {
      const total = lineTotal(l);
      const discount = Number(l.quantity) * Number(l.unitPrice) * (Number(l.discountPct) / 100);
      const vat = lineVat(l);
      return {
        productName: l.name,
        productCode: l.code || undefined,
        quantity: Number(l.quantity),
        unitCode: l.unit,
        unitPrice: Number(l.unitPrice),
        discountPct: Number(l.discountPct),
        discountAmount: Math.round(discount * 100) / 100,
        lineTotal: Math.round(total * 100) / 100,
        vatCategory: l.vatCategory,
        vatPercent: Number(l.vatPercent),
        vatAmount: Math.round(vat * 100) / 100,
      };
    }),
  });

  const handleSaveDraft = async () => {
    setSaving(true);
    setError(null);
    setIssues(null);
    try {
      const result = await api.post<{ id: string }>('/admin/efaktura/invoices', buildPayload());
      navigate(`/efaktura/invoices/${result.id}`);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri čuvanju');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndSubmit = async () => {
    setSaving(true);
    setError(null);
    setIssues(null);
    try {
      const result = await api.post<{ id: string }>('/admin/efaktura/invoices', buildPayload());
      try {
        await api.post(`/admin/efaktura/invoices/${result.id}/submit`);
        navigate(`/efaktura/invoices/${result.id}`);
      } catch (submitErr: any) {
        // Draft je sacuvan, ali validacija/submit je pao - vodimo korisnika
        // na detail stranicu da vidi i resi validation issues.
        if (submitErr?.data?.issues) {
          setIssues(submitErr.data.issues);
          setError(submitErr.data.message || 'Faktura nije validna');
          // ostaje na formi da korisnik popravi
        } else {
          setError(submitErr?.data?.message || submitErr?.message || 'Greška pri slanju');
          navigate(`/efaktura/invoices/${result.id}`);
        }
      }
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri čuvanju');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <header style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <button onClick={() => navigate('/efaktura')} style={iconBtn} title="Nazad">
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>
            {cloneType === '381' ? 'Novo knjižno odobrenje' : cloneType === '383' ? 'Novo knjižno zaduženje' : 'Nova e-faktura'}
          </h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            {cloneType ? `Kreirano iz fakture - referenca na prethodnu je već popunjena.` : 'Kreiranje nove elektronske fakture'}
          </div>
        </div>
      </header>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
      {issues && issues.length > 0 && (
        <div style={{ ...errorBox, display: 'block' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <AlertTriangle size={14} /> Greške validacije:
          </div>
          <ul style={{ margin: 0, paddingLeft: 22, fontSize: 12 }}>
            {issues.map((iss, i) => (
              <li key={i}><strong>{iss.field}:</strong> {iss.message}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Section 1: Tenant + Document type */}
      <Section title="Osnovni podaci">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
          <Field label="Tenant">
            <select value={selectedTenantId} onChange={(e) => setSelectedTenantId(e.target.value)} style={selectStyle}>
              {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Field>
          <Field label="Broj fakture">
            <input type="text" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} style={inputStyle} placeholder="FAKT-001" />
          </Field>
          <Field label="Tip dokumenta">
            <select value={documentType} onChange={(e) => setDocumentType(e.target.value)} style={selectStyle}>
              {documentTypes.map((dt) => <option key={dt.value} value={dt.value}>{dt.value} - {dt.label}</option>)}
            </select>
          </Field>
        </div>
      </Section>

      {/* Section 2: Buyer */}
      <Section title="Kupac">
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <Field label="PIB">
              <input type="text" value={buyerTaxId} onChange={(e) => setBuyerTaxId(e.target.value)} style={inputStyle} placeholder="PIB kupca" />
            </Field>
          </div>
          <button type="button" onClick={handleLookup} disabled={lookupLoading} style={{ ...btnSecondary, marginBottom: 0 }}>
            <Search size={14} /> {lookupLoading ? 'Pretraga...' : 'Pretraži'}
          </button>
        </div>
        <Field label="Naziv">
          <input type="text" value={buyerName} onChange={(e) => setBuyerName(e.target.value)} style={inputStyle} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field label="Matični broj">
            <input type="text" value={buyerRegNumber} onChange={(e) => setBuyerRegNumber(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Email">
            <input type="email" value={buyerEmail} onChange={(e) => setBuyerEmail(e.target.value)} style={inputStyle} />
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
          <Field label="Adresa">
            <input type="text" value={buyerAddress} onChange={(e) => setBuyerAddress(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Grad">
            <input type="text" value={buyerCity} onChange={(e) => setBuyerCity(e.target.value)} style={inputStyle} />
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 20, alignItems: 'center', marginTop: 4 }}>
          <label style={checkboxLabel}>
            <input type="checkbox" checked={isBudgetUser} onChange={(e) => setIsBudgetUser(e.target.checked)} />
            Budžetski korisnik
          </label>
          <label style={checkboxLabel}>
            <input type="checkbox" checked={hasJbkjs} onChange={(e) => setHasJbkjs(e.target.checked)} />
            JBKJS
          </label>
          {hasJbkjs && (
            <input type="text" value={buyerJbkjs} onChange={(e) => setBuyerJbkjs(e.target.value)} placeholder="JBKJS broj" style={{ ...inputStyle, width: 160 }} />
          )}
        </div>
      </Section>

      {/* Section 3: Dates */}
      <Section title="Datumi">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
          <Field label="Datum izdavanja">
            <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Datum dospeća">
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Datum isporuke">
            <input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} style={inputStyle} />
          </Field>
        </div>
      </Section>

      {/* Section 4: Payment */}
      <Section title="Plaćanje">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field label="Način plaćanja">
            <select value={paymentMeansCode} onChange={(e) => setPaymentMeansCode(e.target.value)} style={selectStyle}>
              {paymentMeans.map((pm) => <option key={pm.value} value={pm.value}>{pm.value} - {pm.label}</option>)}
            </select>
          </Field>
          <Field label="Tekući račun">
            <input type="text" value={bankAccount} onChange={(e) => setBankAccount(e.target.value)} style={inputStyle} placeholder="265-1234567890-12" />
          </Field>
        </div>
      </Section>

      {/* Section 5: References */}
      <Section title="Reference">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
          <Field label="Narudžbenica">
            <input type="text" value={orderReference} onChange={(e) => setOrderReference(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Ugovor">
            <input type="text" value={contractReference} onChange={(e) => setContractReference(e.target.value)} style={inputStyle} />
          </Field>
          {(documentType === '381' || documentType === '383') && (
            <Field label="Prethodna faktura">
              <input type="text" value={previousInvoice} onChange={(e) => setPreviousInvoice(e.target.value)} style={inputStyle} />
            </Field>
          )}
        </div>
      </Section>

      {/* Section 6: Line items */}
      <Section title="Stavke">
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead>
              <tr>
                <th style={thStyle}>Naziv</th>
                <th style={{ ...thStyle, width: 80 }}>Šifra</th>
                <th style={{ ...thStyle, width: 70, textAlign: 'right' }}>Količina</th>
                <th style={{ ...thStyle, width: 90 }}>JM</th>
                <th style={{ ...thStyle, width: 90, textAlign: 'right' }}>Cena</th>
                <th style={{ ...thStyle, width: 70, textAlign: 'right' }}>Popust%</th>
                <th style={{ ...thStyle, width: 100 }}>PDV kat.</th>
                <th style={{ ...thStyle, width: 60, textAlign: 'right' }}>PDV%</th>
                <th style={{ ...thStyle, width: 100, textAlign: 'right' }}>Bez PDV</th>
                <th style={{ ...thStyle, width: 90, textAlign: 'right' }}>PDV</th>
                <th style={{ ...thStyle, width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, idx) => (
                <tr key={idx}>
                  <td style={tdStyle}>
                    <input type="text" value={line.name} onChange={(e) => updateLine(idx, 'name', e.target.value)} style={cellInput} placeholder="Naziv stavke" />
                  </td>
                  <td style={tdStyle}>
                    <input type="text" value={line.code} onChange={(e) => updateLine(idx, 'code', e.target.value)} style={cellInput} />
                  </td>
                  <td style={tdStyle}>
                    <input type="number" value={line.quantity} onChange={(e) => updateLine(idx, 'quantity', parseFloat(e.target.value) || 0)} style={{ ...cellInput, textAlign: 'right' }} min={0} step="any" />
                  </td>
                  <td style={tdStyle}>
                    <select value={line.unit} onChange={(e) => updateLine(idx, 'unit', e.target.value)} style={cellInput}>
                      {units.map((u) => <option key={u.value} value={u.value}>{u.value}</option>)}
                    </select>
                  </td>
                  <td style={tdStyle}>
                    <input type="number" value={line.unitPrice} onChange={(e) => updateLine(idx, 'unitPrice', parseFloat(e.target.value) || 0)} style={{ ...cellInput, textAlign: 'right' }} min={0} step="any" />
                  </td>
                  <td style={tdStyle}>
                    <input type="number" value={line.discountPct} onChange={(e) => updateLine(idx, 'discountPct', parseFloat(e.target.value) || 0)} style={{ ...cellInput, textAlign: 'right' }} min={0} max={100} step="any" />
                  </td>
                  <td style={tdStyle}>
                    <select value={line.vatCategory} onChange={(e) => updateLine(idx, 'vatCategory', e.target.value)} style={cellInput}>
                      {vatCategories.map((v) => <option key={v.value} value={v.value}>{v.value}</option>)}
                    </select>
                  </td>
                  <td style={tdStyle}>
                    <input type="number" value={line.vatPercent} onChange={(e) => updateLine(idx, 'vatPercent', parseFloat(e.target.value) || 0)} style={{ ...cellInput, textAlign: 'right' }} min={0} max={100} step="any" />
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontSize: 13, fontVariantNumeric: 'tabular-nums', color: 'var(--color-text-strong)' }}>
                    {formatAmount(lineTotal(line))}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontSize: 13, fontVariantNumeric: 'tabular-nums', color: 'var(--color-text-muted)' }}>
                    {formatAmount(lineVat(line))}
                  </td>
                  <td style={tdStyle}>
                    <button type="button" onClick={() => removeLine(idx)} style={{ ...iconBtn, color: '#fca5a5' }} title="Ukloni">
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button type="button" onClick={() => setLines((prev) => [...prev, emptyLine()])} style={{ ...btnSecondary, marginTop: 8, alignSelf: 'flex-start' }}>
          <Plus size={14} /> Dodaj stavku
        </button>
      </Section>

      {/* Section 7: Totals */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
        <div style={{ background: 'var(--color-bg)', borderRadius: 8, border: '1px solid var(--color-border)', padding: 16, minWidth: 260 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13, color: 'var(--color-text-muted)' }}>
            <span>Total bez PDV:</span>
            <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--color-text-strong)' }}>{formatAmount(totalWithoutVat)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13, color: 'var(--color-text-muted)' }}>
            <span>PDV:</span>
            <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--color-text-strong)' }}>{formatAmount(totalVat)}</span>
          </div>
          <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 700, color: 'var(--color-text-strong)' }}>
            <span>Ukupno:</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAmount(grandTotal)}</span>
          </div>
        </div>
      </div>

      {/* Section 8: Action buttons */}
      <div style={{ display: 'flex', gap: 10, marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--color-border)' }}>
        <button type="button" onClick={() => navigate('/efaktura')} style={btnSecondary}>Otkaži</button>
        <div style={{ flex: 1 }} />
        <button type="button" onClick={handleSaveDraft} disabled={saving} style={{ ...btnSecondary, opacity: saving ? 0.5 : 1 }}>
          <Save size={14} /> {saving ? 'Čuvanje...' : 'Sačuvaj kao nacrt'}
        </button>
        <button type="button" onClick={handleSaveAndSubmit} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.5 : 1 }}>
          <Send size={14} /> {saving ? 'Slanje...' : 'Sačuvaj i pošalji'}
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 14, background: 'var(--color-bg)', borderRadius: 8, border: '1px solid var(--color-border)', marginBottom: 16 }}>
      <h4 style={{ margin: 0, fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</h4>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500 }}>{label}</label>
      {children}
    </div>
  );
}

/* ─── Style constants ─── */
const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, width: '100%', background: 'var(--color-bg-surface)', color: 'var(--color-text)' };
const selectStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, width: '100%', background: 'var(--color-bg-surface)', color: 'var(--color-text)' };
const cellInput: React.CSSProperties = { padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 4, fontSize: 12, width: '100%', background: 'var(--color-bg-surface)', color: 'var(--color-text)' };
const btnPrimary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: 'var(--color-bg-surface)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500 };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 6, borderRadius: 4, cursor: 'pointer' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const checkboxLabel: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--color-text)', cursor: 'pointer' };
const thStyle: React.CSSProperties = { padding: '8px 6px', fontSize: 10, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const tdStyle: React.CSSProperties = { padding: '4px 4px', borderBottom: '1px solid var(--color-border)' };
